import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { InfoModal } from "@/components/site/Modals";
import { Spec } from "@/components/site/Section";
import { useEnquiry } from "@/components/site/EnquiryContext";
import { company, productRange, residentialSystems, commercialSystems } from "@/content/site";

const METRICS = [
  [String(company.established), "Established"],
  [String(company.experienceYears), "Years of experience"],
  [String(residentialSystems.length + commercialSystems.length), "System families"],
  [String(company.googleRating), "Google rating"],
  ["3", "Markets: residential, commercial, high-rise"],
  ["1", "Company from design to installation"],
];

const IN_HOUSE = [
  ["Design", "Detailing and drawings at the drafting desk, matched to the project specification."],
  ["Fabrication", "Profiles cut, machined and assembled in the Vaughan shop."],
  ["Glazing", "Units glazed in the shop before dispatch, handled with vacuum lifters."],
  ["Delivery", "Frames loaded and transported to site on Alumicom trucks."],
  ["Installation", "Alumicom crews install and glaze the system on site."],
  ["Quality assurance", "Workmanship and materials checked before systems leave the shop."],
];

const PERFORMANCE = [
  ["0.64", "W/(m²K)", "Window insulation (Uw) from, MB-79N"],
  ["0.44", "W/m²K", "Door insulation (UD) down to, 95 mm panel"],
  ["0.6", "W/m²K", "Facade frame insulation (Uf) from, MB-TT50"],
  ["600", "kg", "Lift-slide leaf weight up to, MB-77HS"],
  ["81", "mm", "Glazing thickness up to, MB-104 Passive House"],
  ["127", "mm", "Window wall frame depth, 127 Series"],
];

const Heading = ({ children }) => <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-[#3E5468]">{children}</h3>;

// Only facts the owner has provided; undated stages are labelled as such rather than given invented years.
const TIMELINE = [
  [String(company.established), "Alumicom is established", "The company starts out fabricating and installing aluminum systems."],
  ["Growth", "From fabricator to full service", "Over the years Alumicom grows into a full-service design, manufacture and glazing operation for high-rise, commercial and residential projects."],
  ["Range", `${residentialSystems.length + commercialSystems.length} system families`, "Curtain wall, window wall, doors, railings, skylights and cladding for commercial work; windows, entry, lift-slide, sliding and folding doors and Passive House solutions for homes."],
  [String(company.established + company.experienceYears), `${company.experienceYears} years of Alumicom`, `One team from initial concept to final installation, with its own shop in Vaughan and projects across ${company.serviceArea}.`],
];

const Timeline = () => (
  <section className="grid gap-4" data-testid="about-timeline"><Heading>{company.experienceYears} years of Alumicom</Heading>
    <ol className="relative ml-2 border-l-2 border-[#C9D3DC]">
      {TIMELINE.map(([y, t, d], i) => (
        <li key={t} className="tl-item relative pb-7 pl-7 last:pb-0" style={{ animationDelay: `${120 + i * 90}ms` }} data-testid={`about-timeline-item-${i}`}>
          <span className={`absolute -left-[9px] top-1 h-4 w-4 rounded-full border-2 border-white ${i === 0 || i === TIMELINE.length - 1 ? "bg-[#102A43]" : "bg-[#7890A5]"} shadow-[0_0_0_2px_#C9D3DC]`} aria-hidden="true" />
          <p className={`font-bold tabular-nums text-[#102A43] ${/^\d+$/.test(y) ? "text-2xl md:text-3xl" : "text-sm uppercase tracking-[0.14em] text-[#3E5468]"}`}>{y}</p>
          <p className="mt-1 font-semibold text-[#102A43]">{t}</p>
          <p className="mt-1 text-[15px] leading-relaxed">{d}</p>
        </li>
      ))}
    </ol>
  </section>
);

const Metrics = () => (
  <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-[14px] border border-[#D9E1E8] bg-[#D9E1E8] sm:grid-cols-3" data-testid="about-modal-metrics">
    {METRICS.map(([v, l]) => (
      <div key={l} className="bg-white p-4 md:p-5">
        <dt className="sr-only">{l}</dt>
        <dd className="text-3xl font-bold leading-none tabular-nums text-[#102A43] md:text-4xl">{v}</dd>
        <dd className="mt-2 text-sm leading-snug text-[#2B3642]">{l}</dd>
      </div>
    ))}
  </dl>
);

const Performance = () => (
  <ul className="grid gap-3 sm:grid-cols-2">
    {PERFORMANCE.map(([v, u, l]) => (
      <li key={l} className="rounded-[12px] bg-[#102A43] p-4 text-white">
        <p className="text-2xl font-bold tabular-nums">{v} <span className="text-sm font-semibold text-[#C4D2DF]">{u}</span></p>
        <p className="mt-1 text-sm text-[#D6E0E9]">{l}</p>
      </li>
    ))}
  </ul>
);

const AboutBody = ({ onEnquire }) => (
  <div className="grid gap-9">
    <p className="text-base leading-relaxed md:text-lg">
      {company.legalName} is a leading figure in aluminum systems fabrication and installation, established in {company.established}. Over the years it has grown into a full-service design, manufacture and glazing operation for high-rise, commercial and residential projects across {company.serviceArea}. One team handles every step, from initial concept to final installation.
    </p>
    <Metrics />
    <Timeline />
    <section className="grid gap-4"><Heading>What we do in-house</Heading>
      <ol className="grid gap-4 sm:grid-cols-2">
        {IN_HOUSE.map(([t, d], i) => (
          <li key={t} className="border-l-2 border-[#102A43] pl-4">
            <p className="text-sm font-semibold tabular-nums text-[#7890A5]">{String(i + 1).padStart(2, "0")}</p>
            <p className="mt-1 font-semibold text-[#102A43]">{t}</p>
            <p className="mt-1 text-[15px] leading-relaxed">{d}</p>
          </li>
        ))}
      </ol>
    </section>
    <section className="grid gap-4"><Heading>Performance figures from our systems</Heading><Performance />
      <p className="text-xs text-[#4A5866]">Published system values. Lower insulation values mean less heat loss. Final performance is confirmed per project.</p>
    </section>
    <section className="grid gap-4"><Heading>Our range of aluminum systems</Heading>
      <Spec items={productRange} />
    </section>
    <section className="grid gap-6 sm:grid-cols-2">
      <div><Heading>Residential systems</Heading><div className="mt-3"><Spec items={residentialSystems.map((s) => `${s.name} (${s.series})`)} /></div></div>
      <div><Heading>Commercial systems</Heading><div className="mt-3"><Spec items={commercialSystems.map((s) => `${s.name} (${s.series})`)} /></div></div>
    </section>
    <section className="grid gap-6 sm:grid-cols-2">
      <div><Heading>Who we work with</Heading><div className="mt-3"><Spec items={["Homeowners building or renovating", "Architects, designers and custom builders", "Commercial contractors and developers", `Projects across ${company.serviceArea}`]} /></div></div>
      <div><Heading>Facility</Heading>
        <p className="mt-3 text-[15px] leading-relaxed">{company.address.street}, {company.address.city}, {company.address.region} {company.address.postal}</p>
        <ul className="mt-2 grid gap-1 text-[15px]">{company.hours.map(([d, h]) => <li key={d}><span className="font-semibold">{d}:</span> {h}</li>)}</ul>
        <p className="mt-2 text-[15px]">Tel. {company.tel} · Toll free {company.tollFree}</p>
      </div>
    </section>
    <div className="border-t border-[#D9E1E8] pt-6">
      <button type="button" className="btn-glass" onClick={onEnquire} data-testid="about-modal-cta-btn">Discuss your project <ArrowRight aria-hidden="true" size={16} /></button>
    </div>
  </div>
);

export const AboutMore = ({ label = "More about Alumicom", testid = "about-more-btn", className = "mt-10" }) => {
  const [open, setOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  const enquire = () => { setOpen(false); setTimeout(() => openEnquiry("About section — More about Alumicom — Discuss your project", { onReturn: () => setOpen(true) }), 180); };
  return (
    <>
      <div className={className}>
        <button type="button" className="btn-glass" onClick={() => setOpen(true)} data-testid={testid}>{label} <ArrowRight aria-hidden="true" size={16} /></button>
      </div>
      <InfoModal open={open} onOpenChange={setOpen} title="More about Alumicom" eyebrow="Company profile" testid={testid === "about-more-btn" ? "about-modal" : `${testid}-modal`}>
        <AboutBody onEnquire={enquire} />
      </InfoModal>
    </>
  );
};
